import { FOCUSABLE_SELECTOR } from './focusableSelector'

// Used for closing all menus
const megaMenus = document.querySelectorAll('[data-menu]')
const body = document.querySelector("body");

export class Menu {
  /** @type {string | null} */
  menuID = null

  /** @type {HTMLButtonElement | null} */
  menuButtonElement = null

  /** @type {HTMLButtonElement | null} */
  menuBackElement = null

  /** @type {HTMLButtonElement | null} */
  menuCloseElement = null

  /** @type {HTMLButtonElement | null} */
  menuElement = null

  /** @type {Function | null} */
  handleClickOutsideMethod = null

  /** @type {Function | null} */
  handleTabOutMethod = null

  /** @type {Function | null} */
  currentTransitionHandler = null

  /**
   * @param {HTMLElement} menuButtonElement
   * @return {void}
   */
  constructor (menuButtonElement) {
    this.menuButtonElement = menuButtonElement

    this.menuID = this.menuButtonElement.dataset.menuButton
    this.menuElement = document.querySelector(`[data-menu="${this.menuID}"]`)
    this.menuBackElement = this.menuElement.querySelector(`[data-menu-back="${this.menuID}"]`)
    this.menuCloseElementsArray = this.menuElement.querySelectorAll("[data-menu-close]")   

    this.menuCloseElementsArray.forEach(menuCloseButton => {
      menuCloseButton.addEventListener('click', this.handleCloseMenuButton.bind(this))
    })

    this.menuButtonElement.addEventListener('click', this.toggleMenu.bind(this))
    this.menuButtonElement.addEventListener('keypress', this.toggleMenu.bind(this))
    this.menuElement.addEventListener('keyup', this.handleEscapeKey.bind(this))

    if (this.menuBackElement) {
      this.menuBackElement.addEventListener('click', this.handleBackButton.bind(this))
      this.menuBackElement.addEventListener('keypress', this.handleBackButton.bind(this))
    }

    this.menuElement.menu = this
  }

  /**
   * @return {boolean}
   */
  get menuIsOpen () {
    return this.menuButtonElement.getAttribute('aria-expanded') === 'true'
  }

  /**
   * @return {HTMLElement}
   */
  get firstFocusableElement () {
    return this.menuElement.querySelector(FOCUSABLE_SELECTOR)
  }

  /**
   * @return {HTMLElement}
   */
  get lastFocusableElement () {
    return this.menuElement.querySelectorAll(FOCUSABLE_SELECTOR).at(-1)
  }

  /**
   * @return {string}
   */
  get menuActiveClass () {
    return this.menuElement.dataset.activeClass
  }

  /**
   * @param {string} value
   */
  set menuActiveClass (value) {
    this.menuElement.dataset.activeClass = value
  }

  /**
   * @param {string} value
   */
  get menuButtonActiveClass () {
    return this.menuButtonElement.dataset.activeClass
  }

  /**
   * @param {string} value
   */
  set menuButtonActiveClass (value) {
    this.menuButtonElement.dataset.activeClass = value
  }

  /**
   * @return {boolean}
   */
  get willOpenWithTransition () {
    if (!this.menuButtonElement.dataset.openWithTransition) return false

    return this.menuButtonElement.dataset.openWithTransition === 'true'
  }

  /**
   * @param {string} value
   */
  set willOpenWithTransition (value) {
    this.menuButtonElement.dataset.openWithTransition = value
  }

  /**
   * @param {KeyboardEvent | MouseEvent} event
   * @return {void}
   */
  toggleMenu (event) {
    const isKeyboardEvent = event instanceof KeyboardEvent

    if (isKeyboardEvent) {
      event.preventDefault()
    }

    if (this.menuIsOpen) {
      this.close(isKeyboardEvent)
    } else if (this.willOpenWithTransition) {
      this.openWithTransition()
    } else {
      this.open()
    }
  }

  /**
   * @param {boolean} isKeyboardEvent
   * @return {void}
   */
  close (isKeyboardEvent) {
    // Clear any existing transition handler
    // Ensures that the transitionend event is cleared if the menu is opened and closed quickly
    if (this.currentTransitionHandler) {
      this.menuElement.removeEventListener('transitionend', this.currentTransitionHandler);
      this.currentTransitionHandler = null;
    }
    // Waits for the opacity transition to finish before closing menu panel
    const handleTransitionEnd = () => {
      this.menuElement.setAttribute('aria-hidden', 'true')
      this.menuElement.inert = true

      if (isKeyboardEvent) {
        this.menuButtonElement.focus()
      }

      this.menuElement.dispatchEvent(new CustomEvent('menuClose', {
        bubbles: true,
        detail: {
          menu: this.menuElement
        }
      }))
      
      // Remove the event listener after it fires
      this.menuElement.removeEventListener('transitionend', handleTransitionEnd);
      this.currentTransitionHandler = null;
    };

    this.currentTransitionHandler = handleTransitionEnd;
    this.menuElement.addEventListener('transitionend', handleTransitionEnd);

    this.menuElement.classList.remove(this.menuActiveClass)
    this.menuButtonElement.setAttribute('aria-expanded', 'false')
    this.menuButtonElement.classList.remove(this.menuButtonActiveClass)

    document.removeEventListener('click', this.handleClickOutsideMethod)
    document.removeEventListener('keyup', this.handleTabOutMethod)

    if (this.menuButtonElement.dataset.menuButton == 'mobile-menu') {
      body.classList.remove('-scrollLock')
    }
  }

  /**
   * @param {boolean} isKeyboardEvent
   * @return {void}
   */
  open () {
    // Clear any existing transition handler
    if (this.currentTransitionHandler) {
      this.menuElement.removeEventListener('transitionend', this.currentTransitionHandler);
      this.currentTransitionHandler = null;
    }

    this.menuElement.setAttribute('aria-hidden', 'false')
    this.menuButtonElement.setAttribute('aria-expanded', 'true')
    this.menuButtonElement.classList.add(this.menuButtonActiveClass)
    this.menuElement.inert = false
    this.menuElement.classList.add(this.menuActiveClass)

    this.handleClickOutsideMethod = this.handleClickOutside.bind(this)
    this.handleTabOutMethod = this.handleTabOut.bind(this)

    document.addEventListener('click', this.handleClickOutsideMethod)
    document.addEventListener('keyup', this.handleTabOutMethod)

    if (this.menuButtonElement.dataset.menuButton == 'mobile-menu') {
      body.classList.add('-scrollLock')
    }

    this.menuElement.dispatchEvent(new CustomEvent('menuOpen', {
      bubbles: true,
      detail: {
        menu: this.menuElement
      }
    }))
  }

  /**
   * @param {boolean} isKeyboardEvent
   * @return {void}
   */
  openWithTransition () {
    // Clear any existing transition handler
    if (this.currentTransitionHandler) {
      this.menuElement.removeEventListener('transitionend', this.currentTransitionHandler);
      this.currentTransitionHandler = null;
    }

    requestAnimationFrame(() => {
      this.menuElement.setAttribute('aria-hidden', 'false')

      requestAnimationFrame(() => {
        this.menuElement.classList.add(this.menuActiveClass)
        this.menuButtonElement.setAttribute('aria-expanded', 'true')
        this.menuButtonElement.classList.add(this.menuButtonActiveClass)
        this.menuElement.inert = false

        this.handleClickOutsideMethod = this.handleClickOutside.bind(this)
        this.handleTabOutMethod = this.handleTabOut.bind(this)

        document.addEventListener('click', this.handleClickOutsideMethod)
        document.addEventListener('keyup', this.handleTabOutMethod)

        if (this.menuButtonElement.dataset.menuButton == 'mobile-menu') {
          body.classList.add('-scrollLock')
        }

        this.menuButtonElement.dispatchEvent(new CustomEvent('menuOpen', {
          bubbles: true,
          detail: {
            menu: this.menuElement
          }
        }))
      })
    })
  }

  handleCloseMenuButton () {
    megaMenus.forEach(menu => this.close(true))
  }

  /**
   * @param {KeyboardEvent} event
   * @return {void}
   */
  handleEscapeKey (event) {
    if (event.key === 'Escape') {
      event.stopPropagation()
      megaMenus.forEach(menu => this.close(true))
    }
  }

  /**
   * @param {MouseEvent} event
   * @return {void}
   */
  handleClickOutside (event) {
    if (event.target === this.menuButtonElement) return

    if (this.menuIsOpen && !this.menuElement.contains(event.target)) {
      megaMenus.forEach(menu => this.close(true))
    }
  }

  /**
   * @param {KeyboardEvent | MouseEvent} event
   * @return {void}
   */
  handleBackButton (event) {

    event.preventDefault()
    event.stopPropagation()

    const isKeyboardEvent = event instanceof KeyboardEvent

    this.close(isKeyboardEvent)
  }

  /**
   * @param {KeyboardEvent} event
   * @return {void}
   */
  handleTabOut ({ key, target }) {
    if (key === 'Tab') {
      const isChildOfMenu = this.menuElement.contains(target)

      if (!isChildOfMenu) {
        this.close()
      }
    }
  }
}

// @Megan close menu on scroll